/**
 * Pure functions of the `semantic_word` type: the progress blob, the submission and how a
 * server verdict folds back into the progress (ADR 0041). No component here, so `module.ts`
 * stays light in the eager bundle.
 */
import type { Feedback, Verdict } from "../types";
import {
  DEFAULT_TOP_RANK,
  type Guess,
  type SemanticWordDefinition,
  type SemanticWordProgress,
  type SemanticWordSubmission,
} from "./types";

/** Mirrors the server's `normalize_word`: NFC, surrounding spaces removed, lowercase. */
export function normalizeWord(word: string): string {
  return word.normalize("NFC").trim().toLowerCase();
}

export function emptyDefinition(): SemanticWordDefinition {
  return {
    puzzleType: "semantic_word",
    typeVersion: 1,
    language: "fr",
    topRank: DEFAULT_TOP_RANK,
    rulesText: {
      fr: "Trouvez le mot caché. À chaque proposition, la température indique à quel point votre mot est proche du mot caché (100 = trouvé), et son rang s'il fait partie des mots les plus proches. Les mots proches sont ceux qui apparaissent dans les mêmes contextes, pas ceux qui se ressemblent.",
      en: "Find the hidden word. Each guess gets a temperature telling how close it is to the hidden word (100 = found), and its rank when it is among the nearest words. Close words are the ones used in the same contexts, not the ones that look alike.",
    },
  };
}

export function emptyProgress(_definition: SemanticWordDefinition): SemanticWordProgress {
  return { version: 1, draft: "", guesses: [] };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asGuess(value: unknown): Guess | null {
  if (!isRecord(value) || typeof value.word !== "string") return null;
  const similarity = typeof value.similarity === "number" ? value.similarity : NaN;
  if (!Number.isFinite(similarity)) return null;
  const rank =
    typeof value.rank === "number" && Number.isInteger(value.rank) && value.rank >= 0
      ? value.rank
      : null;
  return { word: value.word, similarity, rank };
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(
  definition: SemanticWordDefinition,
  raw: unknown,
): SemanticWordProgress {
  if (!isRecord(raw)) return emptyProgress(definition);
  const guesses = Array.isArray(raw.guesses)
    ? raw.guesses.map(asGuess).filter((g): g is Guess => g !== null)
    : [];
  const unknown = Array.isArray(raw.unknown)
    ? raw.unknown.filter((w): w is string => typeof w === "string")
    : [];
  return {
    version: 1,
    draft: typeof raw.draft === "string" ? raw.draft : "",
    guesses,
    ...(unknown.length > 0 ? { unknown } : {}),
  };
}

/** The draft, trimmed: the server normalises the rest. */
export function serializeSubmission(
  _definition: SemanticWordDefinition,
  progress: SemanticWordProgress,
): SemanticWordSubmission {
  return { word: progress.draft.trim() };
}

/** The guess already graded with this word, if any: the Player refuses it locally. */
export function findGuess(progress: SemanticWordProgress, word: string): Guess | undefined {
  const wanted = normalizeWord(word);
  return progress.guesses.find((guess) => guess.word === wanted);
}

function gradedFeedback(feedback: Feedback[]): Feedback | undefined {
  return feedback.find((item) => item.code === "similarity");
}

/**
 * Folds one verdict into the progress. A graded guess joins the history (once), the found
 * word joins it at 100 / rank 0, an unknown word is listed as such and the draft is kept for
 * the player to fix. Anything else (an empty guess, a malformed one) changes nothing.
 */
export function applyVerdict(
  _definition: SemanticWordDefinition,
  progress: SemanticWordProgress,
  verdict: Verdict<SemanticWordSubmission>,
): SemanticWordProgress {
  const word = normalizeWord(verdict.submission.word);
  if (!word) return progress;
  if (verdict.correct) {
    return {
      ...progress,
      draft: "",
      guesses: [...progress.guesses, { word, similarity: 100, rank: 0 }],
    };
  }
  const graded = gradedFeedback(verdict.feedback);
  if (graded) {
    const params = graded.params ?? {};
    const similarity = typeof params.similarity === "number" ? params.similarity : 0;
    const rank = typeof params.rank === "number" ? params.rank : null;
    const gradedWord = typeof params.word === "string" ? params.word : word;
    if (progress.guesses.some((guess) => guess.word === gradedWord)) {
      return { ...progress, draft: "" };
    }
    return {
      ...progress,
      draft: "",
      guesses: [...progress.guesses, { word: gradedWord, similarity, rank }],
    };
  }
  if (verdict.feedback.some((item) => item.code === "unknown_word")) {
    const unknown = progress.unknown ?? [];
    return {
      ...progress,
      unknown: unknown.includes(word) ? unknown : [...unknown, word],
    };
  }
  return progress;
}

export type Heat = "found" | "hot" | "warm" | "tepid" | "cool" | "cold";

/** Temperature floors of the bands below "hot", which the legend spells out to the player. */
export const HEAT_FLOORS = { warm: 40, tepid: 28, cool: 18 } as const;

/**
 * A band for the eye. Rank beats temperature: being among the nearest words is what
 * matters, and the temperature scale itself depends on the vectors.
 */
export function heatOf(guess: Guess): Heat {
  if (guess.rank === 0) return "found";
  if (guess.rank !== null) return "hot";
  if (guess.similarity >= HEAT_FLOORS.warm) return "warm";
  if (guess.similarity >= HEAT_FLOORS.tepid) return "tepid";
  if (guess.similarity >= HEAT_FLOORS.cool) return "cool";
  return "cold";
}

/** "59.83" split for display: the integer part leads, the decimals are quieter. */
export function splitTemperature(similarity: number): { whole: string; decimals: string } {
  const [whole, decimals = "00"] = similarity.toFixed(2).split(".");
  return { whole, decimals };
}

/** Highest similarity first; ties keep submission order. */
export function sortedGuesses(guesses: Guess[]): { guess: Guess; order: number }[] {
  return guesses
    .map((guess, index) => ({ guess, order: index + 1 }))
    .sort((a, b) => b.guess.similarity - a.guess.similarity || a.order - b.order);
}

/** The guess with the highest similarity, for the status line. */
export function bestGuess(guesses: Guess[]): Guess | undefined {
  return sortedGuesses(guesses)[0]?.guess;
}
